// IMPORT REACT
import React, { useContext, useEffect, useState } from "react";

// IMPORT UI COMPONENTS
import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import {
  CustomAsyncSelect,
  CustomSelect,
  CustomSelectWithoutForm,
} from "../../components/CustomFields/CustomSelect";
import { CustomTextField } from "../../components/CustomFields/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import { useLocation, useNavigate } from "react-router-dom";
import DatePickerComponent from "../../components/CustomFields/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { format, isValid } from "date-fns";
import ReactQuill from "react-quill";
import PhoneInput from "react-phone-number-input";
import logo from "../../assets/images/logo.png";
import {
  GetReferBookAvailabilityDateList,
  GetReferBookTokenDetail,
} from "../../services/ReferBookEntryService";
import { PreferencesContext } from "../../PreferenceContext";
import { getAge, useQuery } from "../../shared/CommonFunction";
import { PAGE_SIZE } from "../../services/common/const";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AlertType } from "../../services/models/AlertType";
import {
  GetReasonForReferenceList,
  GetTeamMemberList,
  GetTeamMemberLocationList,
} from "../../services/TokenService";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

interface IReasonForReference {
  displayOrder: number;
  reasonForReferenceValue: selectOption | null;
  reasonForReferenceLabel: string;
}

interface ReferBook {
  referBookEntryId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;

  fromDoctorId?: number;
  fromDoctorName?: string;
  fromDoctorQualification?: string;
  fromDoctorContactNumber?: string;

  fromLocationId?: number;
  fromLocationName?: string;
  fromLocationCode?: string;
  fromLocationAddress1?: string;
  fromLocationAddress2?: string;
  fromLocationCity?: string;
  fromLocationCountryId?: number;
  fromLocationCountry?: string;
  fromLocationCountryRegionId?: number;
  fromLocationCountryRegion?: string;
  fromLocationPostalCode?: string;
  fromLocationContactNumber?: string;
  fromLocationEmail?: string;

  referDate?: Date | null;

  toDoctorId?: number;
  toDoctorName?: string;
  toDoctorQualification?: string;

  toLocationId?: number;
  toLocationName?: string;
  toLocationCode?: string;
  toLocationAddress1?: string;
  toLocationAddress2?: string;
  toLocationCity?: string;
  toLocationCountryId?: number;
  toLocationCountry?: string;
  toLocationCountryRegionId?: number;
  toLocationCountryRegion?: string;
  toLocationPostalCode?: string;
  toLocationContactNumber?: string;
  toLocationEmail?: string;

  forPatientId?: number;
  forPatientFirstName?: string;
  forPatientLastName?: string;
  forPatientAgeDay?: number | null;
  forPatientAgeMonth?: number | null;
  forPatientAgeYear?: number | null;
  forPatientBirthDate?: string;
  forPatientGender?: selectOption | null;
  forPatientMobileNumber?: string;

  reasonForReferenceColl: Array<IReasonForReference>;

  clinicalNotes?: string;

  isDeleted?: boolean;
  dataManagedBy?: number;
  mode?: string;
}

interface ILocation {
  locationId: number;
  code: string;
  locationName: string;

  area: string | null;
  locationTypeCode: string | null;
  tableName: string | null;
  linkId: number;
  hospitalId: number;
  branchId: number;
  departmentId: number | null;
  specialityId: number | null;
  doctorId: number | null;
  organizationId: number;
  consumerId: number;

  contactPerson: string | null;
  email: string | null;

  address1: string | null;
  address2: string | null;
  city: string | null;
  postalCode: string | null;

  countryId: number;
  countryName: string | null;

  countryRegionId: number;
  countryRegionName: string | null;
  countryRegionLabel: string | null;
  countryLabel: string | null;

  primaryMobileNumber: string | null;
  secondaryMobileNumber: string | null;

  isDeleted: boolean;
  isDefault: boolean;

  label: string;
  value: string;
}

interface LocationData {
  locationColl: Array<ILocation> | null;
  totalCount: number;
}

const initLocationData: LocationData = {
  locationColl: [],
  totalCount: 0,
};

interface BranchDetails {
  consumerId: number;
  organizationId: number;
  branchId: number;
  doctorId: number;
  fullName: string;
  qualification: string;
  branchName: string;
  organizationName: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  email: string;
  mobileNumber: string;
  landLineNumber: string;
}

// SCHEMA FOR RESULTDATA
interface ResultData {
  reasonForReferenceColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  reasonForReferenceColl: [],
  tabTypeColl: [],
  totalCount: 0,
};

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

const ReferBookEntry = () => {
  const genderOptions: selectOption[] = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    { value: "OTHER", label: "Other" },
  ];

  const initialBranchDetails: BranchDetails = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    fullName: "",
    qualification: "",
    doctorId: 0,
    branchName: "",
    organizationName: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    email: "",
    mobileNumber: "",
    landLineNumber: "",
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const smallScreen = useMediaQuery("(max-width:768px)");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [locationData, setLocationData] =
    useState<LocationData>(initLocationData);
  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(
    null
  );
  const [branchDetails, setBranchDetails] =
    useState<BranchDetails>(initialBranchDetails);
  const [reasonForReferenceColl, setReasonForReferenceColl] = useState<any[]>(
    []
  );
  const [doctorSelected, setDoctorSelected] = useState(false);
  const [doctorData, setDoctorData] = useState<any[]>([]);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [referBookDate, setReferBookDate] = useState<any[]>([]);

  const location = useLocation();
  const selectedDoctor = location.state?.selectedDoctor;

  const [param, setParam] = useState({
    ...parameters,
    doctorIds: selectedDoctor,
    teamMemberId: selectedDoctor,
  });

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");

  let currentToken: string = "";

  if (token !== null && token !== undefined) {
    currentToken = token;
  } else {
    navigate("/not-found");
  }

  const initReferBookEntryData: ReferBook = {
    referBookEntryId: 0,
    consumerId: 0,
    organizationId: 0,
    branchId: 0,

    fromDoctorId: 0,
    fromDoctorName: "",
    fromDoctorQualification: "",
    fromDoctorContactNumber: "",
    fromLocationId: 0,
    fromLocationName: "",
    fromLocationCode: "",
    fromLocationAddress1: "",
    fromLocationAddress2: "",
    fromLocationCity: "",
    fromLocationCountryId: 0,
    fromLocationCountry: "",
    fromLocationCountryRegionId: 0,
    fromLocationCountryRegion: "",
    fromLocationPostalCode: "",
    fromLocationContactNumber: "",
    fromLocationEmail: "",

    referDate: new Date(Date.now()) || null,

    toDoctorId: 0,
    toDoctorName: "",
    toDoctorQualification: "",
    toLocationId: 0,
    toLocationName: "",
    toLocationCode: "",
    toLocationAddress1: "",
    toLocationAddress2: "",
    toLocationCity: "",
    toLocationCountryId: 0,
    toLocationCountry: "",
    toLocationCountryRegionId: 0,
    toLocationCountryRegion: "",
    toLocationPostalCode: "",
    toLocationContactNumber: "",
    toLocationEmail: "",

    forPatientId: 0,
    forPatientFirstName: "",
    forPatientLastName: "",
    forPatientAgeDay: null,
    forPatientAgeMonth: null,
    forPatientAgeYear: null,
    forPatientBirthDate: "",
    forPatientGender: null,
    forPatientMobileNumber: "",

    reasonForReferenceColl: [],

    clinicalNotes: "",

    isDeleted: false,
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    referBookEntryId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    organizationId: Yup.number().required(),
    branchId: Yup.number().required(),

    fromDoctorId: Yup.number().nullable(),
    fromDoctorName: Yup.string().required("Ref By Doctor Name is required"),
    fromDoctorQualification: Yup.string().nullable(),
    fromDoctorContactNumber: Yup.string().required(
      "Contact Number is required"
    ),
    toLocationName: Yup.object()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .required("Location is required"),
    referDate: Yup.string().required("Refer Date is required"),

    toDoctorId: Yup.number().nullable(),
    toDoctorName: Yup.string().required("Doctor is required"),
    toDoctorQualification: Yup.string().nullable(),

    forPatientId: Yup.number().nullable(),
    forPatientFirstName: Yup.string().required("First Name is required"),
    forPatientLastName: Yup.string().required("Last Name is required"),
    forPatientAgeYear: Yup.number()
      .nullable()
      .test("age-required", "Age is required", function (value) {
        const { forPatientAgeMonth, forPatientAgeDay } = this.parent;
        return (
          (value && value > 0) ||
          (forPatientAgeMonth && forPatientAgeMonth > 0) ||
          (forPatientAgeDay && forPatientAgeDay > 0)
        );
      }),
    forPatientAgeMonth: Yup.number()
      .nullable()
      .test("age-required", "Age is required", function (value) {
        const { forPatientAgeYear, forPatientAgeDay } = this.parent;
        return (
          (forPatientAgeYear && forPatientAgeYear > 0) ||
          (value && value > 0) ||
          (forPatientAgeDay && forPatientAgeDay > 0)
        );
      }),
    forPatientAgeDay: Yup.number()
      .nullable()
      .test("age-required", "Age is required", function (value) {
        const { forPatientAgeYear, forPatientAgeMonth } = this.parent;
        return (
          (forPatientAgeYear && forPatientAgeYear > 0) ||
          (forPatientAgeMonth && forPatientAgeMonth > 0) ||
          (value && value > 0)
        );
      }),
    forPatientGender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Gender is required"),
    forPatientBirthDate: Yup.string().nullable(),
    forPatientMobileNumber: Yup.string().required("Mobile Number is required"),
    clinicalNotes: Yup.string()
      .max(4000, "Clinical Notes must be less than 4000 characters.")
      .nullable(),

    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initReferBookEntryData,
  });

  const updateDateOfBirth = (years: number, months: number, days: number) => {
    let today = new Date();

    let dob = new Date(
      today.getFullYear() - years,
      today.getMonth() - months,
      today.getDate() - days
    );

    if (dob.getMonth() < 0) {
      dob.setFullYear(dob.getFullYear() - 1);
      dob.setMonth(dob.getMonth() + 12);
    }

    while (dob.getDate() <= 0) {
      dob.setMonth(dob.getMonth() - 1);
      dob.setDate(
        dob.getDate() +
          new Date(dob.getFullYear(), dob.getMonth() + 1, 0).getDate()
      );
    }

    let formattedDate = format(dob, "yyyy-MM-dd");

    methods.setValue("forPatientBirthDate", formattedDate, {
      shouldValidate: true,
    });

    methods.setValue("forPatientAgeYear", years, { shouldValidate: true });
    methods.setValue("forPatientAgeMonth", months, { shouldValidate: true });
    methods.setValue("forPatientAgeDay", days, { shouldValidate: true });
  };

  const handleYearChange = (value: number) => {
    const months = methods.getValues("forPatientAgeMonth");
    const days = methods.getValues("forPatientAgeDay");

    updateDateOfBirth(value, months, days);
  };

  const handleMonthChange = (value: number) => {
    const years = methods.getValues("forPatientAgeYear");
    const days = methods.getValues("forPatientAgeDay");

    updateDateOfBirth(years, value, days);
  };

  const handleDayChange = (value: number) => {
    const years = methods.getValues("forPatientAgeYear");
    const months = methods.getValues("forPatientAgeMonth");

    updateDateOfBirth(years, months, value);
  };

  const getReferBookTokenDetail = async () => {
    try {
      let tokenData: any = null;
      const params = new URLSearchParams();
      params.append("token", currentToken || "");
      const result: any = await GetReferBookTokenDetail(params, currentToken);

      if (result?.data?.data) {
        const data = result.data.data;

        tokenData = {
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          branchId: data.branchId ?? 0,
          doctorId: data.doctorId ?? 0,
          fullName: data.fullName ?? "",
          qualification: data.qualification ?? "",
          branchName: data.branchName ?? "",
          organizationName: data.organizationName ?? "",
          address1: data.address1 ?? "",
          address2: data.address2 ?? "",
          city: data.city ?? "",
          postalCode: data.postalCode ?? "",
          email: data.email ?? "",
          mobileNumber: data.mobileNumber ?? "",
          landLineNumber: data.landLineNumber ?? "",
        };

        setBranchDetails(tokenData);
      }
      return tokenData;
    } catch (e) {
      console.error("Error fetching branch details:", e);
    }
  };

  // GET REASON FOR REFERENCE LIST
  async function fetchReasonForReferenceList(
    searchValue: string,
    oninit: boolean,
    consumerId: string,
    orgId: string,
    branchId: string,
    doctorId?: string,
    locationId?: string
  ) {
    try {
      let itemCollection: any = [];
      let params = new URLSearchParams();
      params.append("consumerId", consumerId);
      params.append("organizationId", orgId);
      params.append("branchId", branchId);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      if (doctorId) {
        params.append("doctorIds", doctorId);
      }
      if (locationId) {
        params.append("locationIds", locationId);
      }
      const itemData: any = await GetReasonForReferenceList(
        params,
        currentToken
      );
      if (itemData?.data?.data?.reasonForReferenceColl) {
        itemCollection = [];
        itemData.data.data.reasonForReferenceColl.forEach((item: any) => {
          if (item.reasonForReferenceDetails) {
            try {
              const details = JSON.parse(item.reasonForReferenceDetails);
              details.forEach((detail: any) => {
                itemCollection.push({
                  reasonForReferenceId: item.reasonForReferenceId,
                  reasonForReferenceDetailId: detail.reasonForReferenceDetailId,
                  label: detail.reasonForReferenceName,
                  value: detail.reasonForReferenceDetailId,
                  reasonForReferenceName: detail.reasonForReferenceName,
                  displayOrder: detail.displayOrder,
                  locationId: item.locationId,
                  doctorId: item.doctorId,
                });
              });
            } catch (e) {
              console.error("Parse error:", e);
            }
          }
        });

        if (oninit) setReasonForReferenceColl(itemCollection);
      }
      return itemCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER ITEM
  async function filterReasonForReferenceList(inputValue: string) {
    const consumerId = branchDetails?.consumerId?.toString() || "";
    const organizationId = branchDetails?.organizationId?.toString() || "";
    const branchId = branchDetails?.branchId?.toString() || "";
    const doctorId = branchDetails?.doctorId
      ? branchDetails.doctorId.toString()
      : "";
    const locationId = selectedLocation?.value?.toString() || "";

    if (
      !consumerId ||
      !organizationId ||
      !branchId ||
      !doctorId ||
      !locationId
    ) {
      return [];
    }

    const result = await fetchReasonForReferenceList(
      inputValue,
      false,
      consumerId,
      organizationId,
      branchId,
      doctorId,
      locationId
    );
    return result;
  }

  // ITEM LOAD ON SEARCH
  const loadReasonforReferenceList = (
    inputValue: string,
    callback: (options: any) => void
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterReasonForReferenceList(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET LIST OF DOCTOR
  const fetchDoctorList = async (
    consumerId: string,
    orgId: string,
    branchId: string
  ) => {
    try {
      let doctorCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", consumerId);
      params.append("orgId", orgId);
      params.append("branchId", branchId);
      params.append("isConsultingDoctor", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);

      const result: any = await GetTeamMemberList(params, currentToken);

      if (result?.data?.data?.teamMemberColl) {
        doctorCollection = result?.data?.data?.teamMemberColl;
        setDoctorData(doctorCollection);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const getLocationByToken = async (
    consumerId: string,
    orgId: string,
    branchId: string,
    teamMemberId: string
  ) => {
    try {
      let params = new URLSearchParams();
      let resultData: any = [];

      params.append("consumerId", consumerId);
      params.append("organizationId", orgId);
      params.append("teamMemberId", teamMemberId?.toString());
      params.append("isContainBranch", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);

      const result: any = await GetTeamMemberLocationList(params, currentToken);
      if (result?.data?.data) {
        resultData = result.data.data.locationColl || [];
        setLocationData(resultData);
      }
      return resultData;
    } catch (e) {
      console.log("err:", e);
    }
  };

  // FORMAT DOCTOR OPTION LABEL WITH QUALIFICATION
  const getDoctorOptionLabel = (doctorOption: any) => {
    if (!doctorOption) return "";
    const doctorName =
      doctorOption?.label ||
      doctorOption?.fullName ||
      doctorOption?.doctorFullName ||
      doctorOption?.doctorName ||
      "";
    const qualification =
      doctorOption?.qualification ||
      doctorOption?.doctorQualification ||
      doctorOption?.qualificationName ||
      "";
    return qualification ? `${doctorName} (${qualification})` : doctorName;
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (value: any) => {
    setIsLoading(true);
    let formData = {
      referBookEntryId: value?.referBookEntryId || 0,

      consumerId: branchDetails?.consumerId || 0,
      organizationId: branchDetails?.organizationId || 0,
      branchId: branchDetails?.branchId || 0,

      doctorId: branchDetails?.doctorId || 0,
      doctorFullName: branchDetails?.fullName || "",
      doctorQualification: branchDetails?.qualification || "",

      organizationName: branchDetails?.organizationName || "",

      fromDoctorId: value?.fromDoctorId || 0,
      fromDoctorName: value?.fromDoctorName || "",
      fromDoctorQualification: value?.fromDoctorQualification || "",
      fromDoctorContactNumber: value?.fromDoctorContactNumber || "",

      fromLocationId: value?.fromLocationId || 0,
      fromLocationName: value?.fromLocationName || "",
      fromLocationCode: value?.fromLocationCode || "",
      fromLocationAddress1: value?.fromLocationName || "",
      fromLocationAddress2: value?.fromLocationName || "",
      fromLocationCity: value?.fromLocationName || "",
      fromLocationCountryId: value?.fromLocationCountryId || 0,
      fromLocationCountry: value?.fromLocationName || "",
      fromLocationCountryRegionId: value?.fromLocationCountryRegionId || 0,
      fromLocationCountryRegion: value?.fromLocationName || "",
      fromLocationPostalCode: value?.fromLocationName || "",
      fromLocationContactNumber: value?.fromLocationName || "",
      fromLocationEmail: value?.fromLocationEmail || "",

      referDate: value?.referDate
        ? dayjs(value?.referDate).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),

      toDoctorId: branchDetails?.doctorId || 0,
      toDoctorName: branchDetails?.fullName || "",
      toDoctorQualification: branchDetails?.qualification || "",

      toLocationId: selectedLocation?.locationId || 0,
      toLocationName: selectedLocation?.locationName || "",
      toLocationTypeCode: selectedLocation?.locationTypeCode || "",
      // toLocationName: selectedLocation?.locationName?.split(" (")[0] || "",
      toLocationCode: selectedLocation?.code || "",
      toLocationAddress1: selectedLocation?.address1 || "",
      toLocationAddress2: selectedLocation?.address2 || "",
      toLocationCity: selectedLocation?.city || "",
      toLocationCountryId: selectedLocation?.countryId || 0,
      toLocationCountry: selectedLocation?.countryName || "",
      toLocationCountryRegionId: selectedLocation?.countryRegionId || 0,
      toLocationCountryRegion: selectedLocation?.countryRegionName || "",
      toLocationPostalCode: selectedLocation?.postalCode || "",
      toLocationContactNumber: selectedLocation?.primaryMobileNumber || "",
      toLocationEmail: selectedLocation?.email || "",

      forPatientId: value?.forPatientId || 0,
      forPatientFirstName: value?.forPatientFirstName || "",
      forPatientLastName: value?.forPatientLastName || "",
      forPatientAgeDay: value?.forPatientAgeDay || 0,
      forPatientAgeMonth: value?.forPatientAgeMonth || 0,
      forPatientAgeYear: value?.forPatientAgeYear || 0,
      forPatientBirthDate: value?.forPatientBirthDate || "",
      forPatientGender: value.forPatientGender?.value || "",
      forPatientMobileNumber: value?.forPatientMobileNumber || "",

      reasonForReferenceColl: value?.reasonForReferenceColl || [],
      referBookDate: referBookDate || [],
      clinicalNotes: value?.clinicalNotes || "",

      isDeleted: value?.isDeleted || false,
      dataManagedBy: userContext?.loginDetail?.userId || 0,
    };
    localStorage.setItem(currentToken, JSON.stringify(formData));
    navigate(`/refer-book-preview?t=${currentToken}`);
    setIsLoading(false);
  };

  async function fetchReferBookAvailabilityDate(
    locationId: any,
    consumerId: number,
    organizationId: number,
    branchId: number,
    doctorId: number,
    isOutReach: boolean
  ) {
    try {
      let params = new URLSearchParams();
      let dateCollection = [];
      params.append("consumerId", consumerId?.toString());
      params.append("organizationId", organizationId?.toString());
      params.append("branchId", branchId?.toString());
      params.append("locationId", locationId?.toString());
      params.append("teamMemberId", doctorId?.toString() || "");
      params.append("daysAhead", "3");
      params.append("isOutReach", isOutReach?.toString());
      const response: any = await GetReferBookAvailabilityDateList(
        params,
        currentToken
      );
      if (response?.data?.data?.appointmentAvailabilityDateColl) {
        dateCollection = response.data.data.appointmentAvailabilityDateColl;
        setReferBookDate(dateCollection);
      }
      return dateCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FETCH DATA
  const fetchData = async () => {
    try {
      // await fetchDoctorList();
      let refBook = null;
      let jsonData = localStorage.getItem(currentToken) || "";
      if (jsonData) {
        refBook = JSON.parse(jsonData || "");
      }
      if (refBook) {
        await getLocationByToken(
          refBook.consumerId,
          refBook.organizationId,
          refBook.branchId,
          refBook.doctorId
        );
        await fetchReasonForReferenceList(
          "",
          true,
          refBook.consumerId,
          refBook.organizationId,
          refBook.branchId,
          refBook.doctorId ? refBook.doctorId.toString() : ""
        );
        await fetchDoctorList(
          refBook.consumerId,
          refBook.organizationId,
          refBook.branchId
        );
        
        const latestDates = await fetchReferBookAvailabilityDate(
          refBook?.toLocationId,
          refBook.consumerId,
          refBook.organizationId,
          refBook.branchId,
          refBook.doctorId,
          refBook?.toLocationTypeCode === "OUTREACH" ? true : false
        );

        const restoredData = {
          ...refBook,
          referBookDate: latestDates || [],
          forPatientGender: refBook.forPatientGender
            ? {
                label: refBook.forPatientGender,
                value: refBook.forPatientGender,
              }
            : null,
          toLocationName: refBook.toLocationName
            ? {
                label: refBook.toLocationName,
                value: refBook.toLocationId || 0,
                locationId: refBook.toLocationId || 0,
                code: refBook.toLocationCode || "",
                locationName: refBook.toLocationName || "",
                locationTypeCode: refBook.toLocationTypeCode || "",
                address1: refBook.toLocationAddress1 || "",
                address2: refBook.toLocationAddress2 || "",
                city: refBook.toLocationCity || "",
                postalCode: refBook.toLocationPostalCode || "",
                primaryMobileNumber: refBook.toLocationContactNumber || "",
                email: refBook.toLocationEmail || "",
                countryId: refBook.countryId || 0,
                countryName: refBook.toLocationCountry || "",
                countryRegionId: refBook.countryRegionId || 0,
                countryRegionName: refBook.toLocationCountryRegion || "",
              }
            : null,
        };

        if (
          restoredData.toLocationName ||
          restoredData.doctorFullName ||
          restoredData.doctorQualification
        ) {
          if (restoredData.toLocationName) {
            setSelectedLocation(restoredData.toLocationName);
          }
          setBranchDetails((prev: any) => ({
            ...prev,
            fullName: restoredData.doctorFullName || prev.fullName || "",
            qualification:
              restoredData.doctorQualification || prev.qualification || "",
            organizationName:
              restoredData.organizationName || prev.organizationName || "",

            consumerId: refBook.consumerId || prev.consumerId,
            organizationId: refBook.organizationId || prev.organizationId,
            branchId: refBook.branchId || prev.branchId,
            doctorId: refBook.doctorId || prev.doctorId,
          }));

          if (restoredData.doctorId && restoredData.doctorFullName) {
            setParam((prev: any) => ({
              ...prev,
              doctorIds: {
                value: restoredData.doctorId,
                label: restoredData.doctorFullName,
                qualification: restoredData.doctorQualification || "",
              },
            }));
          }
        }
        methods.reset(restoredData);
      } else {
        const result = await getReferBookTokenDetail();
        if (result) {
          await getLocationByToken(
            result.consumerId,
            result.organizationId,
            result.branchId,
            result.doctorId
          );
        }
        await fetchReasonForReferenceList(
          "",
          true,
          result.consumerId,
          result.organizationId,
          result.branchId,
          result.doctorId ? result.doctorId.toString() : ""
        );
        await fetchDoctorList(
          result.consumerId,
          result.organizationId,
          result.branchId
        );
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // AUTO SELECT LOCATION
  if (
    locationData?.length === 1 &&
    doctorData?.length === 1 &&
    !selectedLocation
  ) {
    const onlyLocation = locationData[0];

    setSelectedLocation(onlyLocation);

    methods.setValue("toLocationName", onlyLocation, { shouldValidate: true });
    methods.setValue("toLocationId", onlyLocation, { shouldValidate: true });
    methods.setValue("toLocationCode", onlyLocation, { shouldValidate: true });
  }

  // AUTO SELECT DOCTOR
  if (doctorData?.length === 1 && !param?.doctorIds) {
    const onlyDoctor = doctorData[0];

    const updatedBranchDetails = {
      ...branchDetails,
      doctorId: onlyDoctor?.value || 0,
      fullName: onlyDoctor?.label || "",
      qualification: onlyDoctor?.qualification || "",
    };

    setParam((prev: any) => ({
      ...prev,
      doctorIds: onlyDoctor,
    }));

    setBranchDetails(updatedBranchDetails);

    methods.setValue("toDoctorId", onlyDoctor?.value || 0, {
      shouldValidate: true,
    });
    methods.setValue("toDoctorName", onlyDoctor?.label || "", {
      shouldValidate: true,
    });
    methods.setValue("toDoctorQualification", onlyDoctor?.qualification || "", {
      shouldValidate: true,
    });

    // Same as inside handleChange ---- VERY IMPORTANT
    if (locationData?.length > 1) {
      setSelectedLocation(null);
      methods.setValue("toLocationName", null);
      methods.setValue("toLocationId", 0);
      methods.setValue("toLocationCode", "");
    }

    methods.setValue("reasonForReferenceColl", []);
    setReasonForReferenceColl([]);

    // Fetch location & reference reason automatically
    if (
      updatedBranchDetails?.doctorId &&
      branchDetails?.consumerId &&
      branchDetails?.organizationId &&
      branchDetails?.branchId
    ) {
      getLocationByToken(
        branchDetails.consumerId.toString(),
        branchDetails.organizationId.toString(),
        branchDetails.branchId.toString(),
        updatedBranchDetails.doctorId.toString()
      );

      fetchReasonForReferenceList(
        "",
        true,
        branchDetails.consumerId.toString(),
        branchDetails.organizationId.toString(),
        branchDetails.branchId.toString(),
        updatedBranchDetails.doctorId.toString()
      ).then((res) => setReasonForReferenceColl(res || []));
    }
  }

  // FORMAT TIME UTILITY
  const formatTime = (time: string) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          marginTop: "30px",
          marginBottom: "30px",
        }}
      >
        <Box
          className={"main-container"}
          sx={{
            width: "100%",
            maxWidth: 900,
          }}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} action="">
              <Grid container gap={2}>
                <Grid item xs={12} className={"group-container"}>
                  {/* HEADER */}
                  <Box textAlign="center" mb={2.5}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "18px !important",
                      }}
                    >
                      {branchDetails?.organizationName}
                    </Typography>
                  </Box>
                  <Divider sx={{ mx: -3, mb: 2 }} />

                  <Grid item xs={12} sm={6} mb={2}>
                    <CustomSelectWithoutForm
                      placeholder="Select Doctor"
                      id={"toDoctorName"}
                      FieldName={"toDoctorName"}
                      autoFocus
                      menuPortalTarget={document.querySelector("body")}
                      handleChange={async (e: any) => {
                        const updatedBranchDetails = {
                          ...branchDetails,
                          doctorId: e?.value || 0,
                          fullName: e?.label || "",
                          qualification: e?.qualification || "",
                        };

                        setParam((prev: any) => ({
                          ...prev,
                          doctorIds: e,
                        }));

                        setBranchDetails(updatedBranchDetails);

                        methods.setValue("toDoctorId", e?.value || 0, {
                          shouldValidate: true,
                        });
                        methods.setValue("toDoctorName", e?.label || "", {
                          shouldValidate: true,
                        });
                        methods.setValue(
                          "toDoctorQualification",
                          e?.qualification || "",
                          {
                            shouldValidate: false,
                          }
                        );

                        // Clear previously selected location and reason data when doctor changes
                        setSelectedLocation(null);
                        methods.setValue("toLocationName", null, {
                          shouldValidate: true,
                        });
                        methods.setValue("toLocationId", 0, {
                          shouldValidate: true,
                        });
                        methods.setValue("toLocationCode", "", {
                          shouldValidate: true,
                        });
                        methods.setValue("reasonForReferenceColl", [], {
                          shouldValidate: true,
                        });
                        setReasonForReferenceColl([]);

                        // Only fetch dependent dropdown data when required ids are available
                        if (
                          branchDetails?.consumerId &&
                          branchDetails?.organizationId &&
                          branchDetails?.branchId &&
                          updatedBranchDetails?.doctorId
                        ) {
                          await getLocationByToken(
                            branchDetails?.consumerId.toString(),
                            branchDetails?.organizationId.toString(),
                            branchDetails?.branchId.toString(),
                            updatedBranchDetails?.doctorId.toString()
                          );

                          const items = await fetchReasonForReferenceList(
                            "",
                            true,
                            branchDetails?.consumerId.toString(),
                            branchDetails?.organizationId.toString(),
                            branchDetails?.branchId.toString(),
                            updatedBranchDetails?.doctorId.toString()
                          );

                          setReasonForReferenceColl(items || []);
                        }

                        // AUTO SELECT LOCATION
                        if (locationData?.length === 1) {
                          const onlyLoc = locationData[0];

                          setSelectedLocation(onlyLoc);

                          methods.setValue("toLocationName", onlyLoc, {
                            shouldValidate: true,
                          });
                          methods.setValue("toLocationId", onlyLoc.value || 0, {
                            shouldValidate: true,
                          });
                          methods.setValue(
                            "toLocationCode",
                            onlyLoc.code || "",
                            { shouldValidate: true }
                          );
                        }

                        setDoctorSelected(false);
                      }}
                      value={param.doctorIds}
                      options={doctorData}
                      getOptionLabel={(option: any) =>
                        getDoctorOptionLabel(option)
                      }
                      isClearable
                      isSearchable
                      isNotCreatable
                    />
                    <Typography className="validation-message">
                      {methods.formState.errors.toDoctorName?.message}
                    </Typography>
                  </Grid>

                  <Divider sx={{ mx: -3, mb: 2 }} />
                  <Grid item container xs={12} spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.toLocationName
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Location *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name={"toLocationName"}
                            render={({ field }) => (
                              <CustomSelect
                                {...field}
                                isNotCreatable={true}
                                id={"toLocationName"}
                                placeholder="Select Location"
                                FieldName={"toLocationName"}
                                options={locationData}
                                openMenuOnFocus
                                isClearable
                                isSearchable
                                menuPortalTarget={document.querySelector(
                                  "body"
                                )}
                                handleChange={async (e: any) => {
                                  if (
                                    methods
                                      ?.getValues()
                                      ?.toLocationName?.value?.toString() !==
                                    e?.value?.toString()
                                  ) {
                                    methods.setValue(
                                      "toLocationId",
                                      e?.value ? e : 0,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                    methods.setValue(
                                      "toLocationName",
                                      e?.label ? e : null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                    methods.setValue(
                                      "toLocationCode",
                                      e?.code ? e : null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                    setSelectedLocation(e);
                                    setReferBookDate([]);
                                    if (e?.value) {
                                      await fetchReferBookAvailabilityDate(
                                        e?.value,
                                        branchDetails?.consumerId,
                                        branchDetails?.organizationId,
                                        branchDetails?.branchId,
                                        branchDetails?.doctorId,
                                        e?.locationTypeCode === "OUTREACH"
                                          ? true
                                          : false
                                      );
                                    } else {
                                      setReferBookDate([]);
                                    }
                                    methods.setValue(
                                      "reasonForReferenceColl",
                                      [],
                                      { shouldValidate: true }
                                    );
                                    setReasonForReferenceColl([]);
                                    if (
                                      branchDetails?.consumerId &&
                                      branchDetails?.organizationId &&
                                      branchDetails?.branchId &&
                                      branchDetails?.doctorId &&
                                      e?.value
                                    ) {
                                      const items =
                                        await fetchReasonForReferenceList(
                                          "",
                                          true,
                                          branchDetails.consumerId.toString(),
                                          branchDetails.organizationId.toString(),
                                          branchDetails.branchId.toString(),
                                          branchDetails.doctorId.toString(),
                                          e.value.toString()
                                        );

                                      setReasonForReferenceColl(items || []);
                                    }
                                  }
                                }}
                                className={`text-input ${
                                  methods.formState.errors.toLocationName
                                    ? "error"
                                    : ""
                                }`}
                              />
                            )}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {methods.formState.errors.toLocationName?.message}
                        </Typography>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}></Grid>

                    <Grid item xs={12}>
                      {selectedLocation && (
                        <Box textAlign="left" mt={1}>
                          {/* Location */}
                          <Typography fontWeight={600} fontSize="14px" mb={0.5}>
                            {selectedLocation.locationName}
                          </Typography>

                          {/* Address */}
                          <Typography>
                            {[
                              selectedLocation.address1,
                              selectedLocation.address2,
                              selectedLocation.city,
                              selectedLocation.countryName
                                ? `${selectedLocation.countryName} - ${selectedLocation.postalCode}`
                                : selectedLocation.postalCode,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </Typography>

                          {/* Mobile Numbers */}
                          {(selectedLocation.primaryMobileNumber ||
                            selectedLocation.secondaryMobileNumber ||
                            selectedLocation.email) && (
                            <Typography mt={0.5}>
                              {[
                                selectedLocation.primaryMobileNumber
                                  ? `Contact Number: ${selectedLocation.primaryMobileNumber}`
                                  : "",
                                selectedLocation.secondaryMobileNumber
                                  ? `${selectedLocation.secondaryMobileNumber}`
                                  : "",
                                selectedLocation.email
                                  ? `Email: ${selectedLocation.email}`
                                  : "",
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </Typography>
                          )}

                          {/* Outreach Schedule Info */}
                          {referBookDate.length > 0 && (
                            <Box mt={1}>
                              <Typography
                                fontWeight={600}
                                fontSize="14px"
                                mb={0.5}
                              >
                                Next working day/hours:
                              </Typography>
                              {referBookDate.map((item: any, index: number) => (
                                <Typography key={index} fontSize="13px">
                                  {item.dayName},{" "}
                                  {dayjs(item.appointmentDate).format(
                                    "MMM DD, YYYY"
                                  )}{" "}
                                  - {item.timeSlot}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ mx: -3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.referDate
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Ref Date *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <DatePickerComponent
                            disabled={true}
                            mode="date"
                            slotProps={{ textField: { size: "small" } }}
                            value={
                              methods?.getValues()?.referDate
                                ? dayjs(methods?.getValues()?.referDate)
                                : null
                            }
                            onChange={(e: any) => {
                              if (e !== null && isValid(e.$d)) {
                                let savedDateFormate: any = format(
                                  e.$d,
                                  "yyyy-MM-dd"
                                );
                                methods.setValue("referDate", savedDateFormate);
                              } else {
                                methods.setValue("referDate", null);
                              }
                            }}
                            format="DD/MM/YYYY"
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.referDate?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}></Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.forPatientFirstName
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Patient First Name *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("forPatientFirstName", {
                              onChange: (e) => {
                                const input = e.target;
                                const selectionStart = input.selectionStart;
                                const selectionEnd = input.selectionEnd;

                                // CONVERT INPUT INTO UPPERCASE LATTERS
                                e.target.value = input.value
                                  ? input.value.toUpperCase()
                                  : "";

                                // Restore the cursor position
                                setTimeout(() => {
                                  input.setSelectionRange(
                                    selectionStart,
                                    selectionEnd
                                  );
                                });

                                methods.setValue(
                                  "forPatientFirstName",
                                  e.target.value,
                                  {
                                    shouldValidate: true,
                                  }
                                ); // UPDATE VALUE
                              },
                            })}
                            error={
                              !!methods.formState.errors.forPatientFirstName
                            }
                            className={`text-input ${
                              methods.formState.errors.forPatientFirstName
                                ? "error"
                                : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.forPatientFirstName?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.forPatientLastName
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Patient Last Name *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("forPatientLastName", {
                              onChange: (e) => {
                                const input = e.target;
                                const selectionStart = input.selectionStart;
                                const selectionEnd = input.selectionEnd;

                                // CONVERT INPUT INTO UPPERCASE LATTERS
                                e.target.value = input.value
                                  ? input.value.toUpperCase()
                                  : "";

                                // Restore the cursor position
                                setTimeout(() => {
                                  input.setSelectionRange(
                                    selectionStart,
                                    selectionEnd
                                  );
                                });

                                methods.setValue(
                                  "forPatientLastName",
                                  e.target.value,
                                  {
                                    shouldValidate: true,
                                  }
                                ); // UPDATE VALUE
                              },
                            })}
                            error={
                              !!methods.formState.errors.forPatientLastName
                            }
                            className={`text-input ${
                              methods.formState.errors.forPatientLastName
                                ? "error"
                                : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.forPatientLastName?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.forPatientBirthDate
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Date of Birth
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <DatePickerComponent
                            format="DD-MM-YYYY"
                            mode="date"
                            slotProps={{ textField: { size: "small" } }}
                            value={
                              methods?.getValues()?.forPatientBirthDate
                                ? dayjs(
                                    methods?.getValues()?.forPatientBirthDate
                                  )
                                : null
                            }
                            maxDate={dayjs()}
                            // prithavi working on this
                            maxDate={dayjs()}
                            onChange={(e: any) => {
                              if (e !== null && isValid(e.$d)) {
                                let birthDate = new Date(e.$d);

                                let { Year, Month, Day } = getAge(birthDate);

                                let savedDateFormate = format(
                                  e.$d,
                                  "yyyy-MM-dd"
                                );
                                methods.setValue(
                                  "forPatientBirthDate",
                                  savedDateFormate
                                );
                                methods.setValue("forPatientAgeYear", Year);
                                methods.setValue("forPatientAgeMonth", Month);
                                methods.setValue("forPatientAgeDay", Day);
                              } else {
                                methods.setValue("forPatientBirthDate", "");
                              }
                            }}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {
                            methods.formState.errors.forPatientBirthDate
                              ?.message
                          }
                        </Typography>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.forPatientAgeYear
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Age *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Grid container gap={1}>
                            <Grid item xs={3} sm={3} md={3}>
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("forPatientAgeYear", {
                                  onChange: (e: any) =>
                                    handleYearChange(
                                      parseInt(e.target.value, 10) || 0
                                    ),
                                })}
                                error={
                                  !!methods.formState.errors.forPatientAgeYear
                                }
                                className={`text-input `}
                                inputProps={{
                                  maxLength: 2,
                                }}
                                InputProps={{
                                  onKeyPress: (event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  },
                                  endAdornment: (
                                    <span style={{ color: "grey" }}>Y</span>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={3} sm={3} md={3}>
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("forPatientAgeMonth", {
                                  onChange: (e: any) =>
                                    handleMonthChange(
                                      parseInt(e.target.value, 10) || 0
                                    ),
                                })}
                                error={
                                  !!methods.formState.errors.forPatientAgeMonth
                                }
                                className={`text-input `}
                                inputProps={{
                                  maxLength: 2,
                                }}
                                InputProps={{
                                  onKeyPress: (event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  },
                                  endAdornment: (
                                    <span style={{ color: "grey" }}>M</span>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={3} sm={3} md={3}>
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("forPatientAgeDay", {
                                  onChange: (e: any) =>
                                    handleDayChange(
                                      parseInt(e.target.value, 10) || 0
                                    ),
                                })}
                                error={
                                  !!methods.formState.errors.forPatientAgeDay
                                }
                                className={`text-input `}
                                inputProps={{
                                  maxLength: 2,
                                }}
                                InputProps={{
                                  onKeyPress: (event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  },
                                  endAdornment: (
                                    <span style={{ color: "grey" }}>D</span>
                                  ),
                                }}
                              />
                            </Grid>
                          </Grid>
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.forPatientAgeYear?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.forPatientGender
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Gender *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="forPatientGender"
                            render={({ field }) => (
                              <CustomSelect
                                {...field}
                                isNotCreatable={true}
                                id={"forPatientGender"}
                                FieldName={"forPatientGender"}
                                options={genderOptions}
                                openMenuOnFocus
                                isClearable
                                isSearchable
                                menuPortalTarget={document.querySelector(
                                  "body"
                                )}
                                handleChange={(e: any) => {
                                  if (
                                    methods
                                      ?.getValues()
                                      ?.forPatientGender?.value?.toString() !==
                                    e?.value?.toString()
                                  ) {
                                    methods.setValue(
                                      "forPatientGender",
                                      e?.value ? e : null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                  }
                                }}
                              />
                            )}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {methods.formState.errors.forPatientGender?.message}
                        </Typography>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <div className="input-group">
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.forPatientMobileNumber
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {/* <WhatsAppIcon sx={{ fontSize: 18 }} /> */}
                            Patient Mobile Number (WhatsApp)
                          </Box>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <PhoneInput
                            id="forPatientMobileNumber"
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry="IN"
                            {...methods.register("forPatientMobileNumber")}
                            error={
                              methods.formState.errors.forPatientMobileNumber
                                ?.message
                            }
                            className={`phone-input ${
                              methods.formState.errors.forPatientMobileNumber
                                ? "error"
                                : ""
                            }`}
                            onChange={(e) => {
                              methods.setValue(
                                "forPatientMobileNumber",
                                e || "",
                                {
                                  shouldValidate: true,
                                }
                              );
                            }}
                            value={methods.getValues()?.forPatientMobileNumber}
                            placeholder="Enter phone number"
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {
                            methods.formState.errors.forPatientMobileNumber
                              ?.message
                          }
                        </Typography>
                      </div>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <Divider sx={{ mx: -3, mt: 1, mb: 2 }} />
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.reasonForReferenceColl
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Reason for Reference
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name={`reasonForReferenceColl`}
                            render={({ field }) => (
                              <CustomAsyncSelect
                                {...field}
                                id={`reasonForReferenceColl`}
                                FieldName={`reasonForReferenceColl`}
                                isMulti
                                placeholder="Search Reason for Reference"
                                defaultOptions={reasonForReferenceColl}
                                openMenuOnFocus
                                isClearable
                                isSearchable
                                hideSelectedOptions
                                onSelectResetsInput={false}
                                closeMenuOnSelect={false}
                                isNotCreatable
                                loadOptions={loadReasonforReferenceList}
                                menuPortalTarget={document.querySelector(
                                  "body"
                                )}
                                handleChange={(e: any) => {
                                  const visitColl =
                                    e?.map((item: any, index: number) => {
                                      return {
                                        ...item,
                                        displayOrder: index,
                                      };
                                    }) || [];
                                  methods.setValue(
                                    "reasonForReferenceColl",
                                    visitColl,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }}
                                // handleChange={async (e: any) => {
                                //   // Only update the reason selection inside form
                                //   const visitColl =
                                //     e?.map((item: any, index: number) => ({
                                //       ...item,
                                //       displayOrder: index,
                                //     })) || [];

                                //   methods.setValue("reasonForReferenceColl", visitColl, {
                                //     shouldValidate: true,
                                //   });
                                // }}
                              />
                            )}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {
                            methods.formState.errors.reasonForReferenceColl
                              ?.message
                          }
                        </Typography>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.clinicalNotes
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Clinical Notes
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-box"
                        >
                          <Controller
                            control={methods.control}
                            name={"clinicalNotes"}
                            render={({ field }) => (
                              <>
                                <style>
                                  {`
                                    .react-quill-note .ql-editor {
                                      height: 150px !important;
                                      overflow-y: auto;
                                      white-space: pre-wrap !important;
                                      word-break: break-word !important;
                                    }
                                `}
                                </style>
                                <ReactQuill
                                  {...field}
                                  theme="snow"
                                  value={methods?.getValues()?.clinicalNotes}
                                  className="react-quill-note"
                                />
                              </>
                            )}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.clinicalNotes?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ mx: -3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.fromDoctorName
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Ref By Doctor (Your Name) *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("fromDoctorName", {
                              onChange: (e) => {
                                const input = e.target;
                                const selectionStart = input.selectionStart;
                                const selectionEnd = input.selectionEnd;

                                // CONVERT INPUT INTO UPPERCASE LATTERS
                                e.target.value = input.value
                                  ? input.value.toUpperCase()
                                  : "";

                                // Restore the cursor position
                                setTimeout(() => {
                                  input.setSelectionRange(
                                    selectionStart,
                                    selectionEnd
                                  );
                                });

                                methods.setValue(
                                  "fromDoctorName",
                                  e.target.value,
                                  {
                                    shouldValidate: true,
                                  }
                                ); // UPDATE VALUE
                              },
                            })}
                            error={!!methods.formState.errors.fromDoctorName}
                            className={`text-input ${
                              methods.formState.errors.fromDoctorName
                                ? "error"
                                : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.fromDoctorName?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.fromDoctorQualification
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Qualification
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("fromDoctorQualification", {
                              onChange: (e) => {
                                const input = e.target;
                                const selectionStart = input.selectionStart;
                                const selectionEnd = input.selectionEnd;

                                // CONVERT INPUT INTO UPPERCASE LATTERS
                                e.target.value = input.value
                                  ? input.value.toUpperCase()
                                  : "";

                                // Restore the cursor position
                                setTimeout(() => {
                                  input.setSelectionRange(
                                    selectionStart,
                                    selectionEnd
                                  );
                                });

                                methods.setValue(
                                  "fromDoctorQualification",
                                  e.target.value,
                                  {
                                    shouldValidate: true,
                                  }
                                ); // UPDATE VALUE
                              },
                            })}
                            error={
                              !!methods.formState.errors.fromDoctorQualification
                            }
                            className={`text-input ${
                              methods.formState.errors.fromDoctorQualification
                                ? "error"
                                : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {
                          methods.formState.errors.fromDoctorQualification
                            ?.message
                        }
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.fromDoctorContactNumber
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Contact Number *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <PhoneInput
                          id="fromDoctorContactNumber"
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="IN"
                          {...methods.register("fromDoctorContactNumber")}
                          error={
                            methods.formState.errors.fromDoctorContactNumber
                              ?.message
                          }
                          className={`phone-input ${
                            methods.formState.errors.fromDoctorContactNumber
                              ? "error"
                              : ""
                          }`}
                          onChange={(e) => {
                            methods.setValue(
                              "fromDoctorContactNumber",
                              e || "",
                              {
                                shouldValidate: true,
                              }
                            );
                          }}
                          value={methods.getValues()?.fromDoctorContactNumber}
                          placeholder="Enter phone number"
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {
                          methods.formState.errors.fromDoctorContactNumber
                            ?.message
                        }
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} mt={3}>
                    <Box
                      sx={{ display: "flex", justifyContent: "end", gap: 2 }}
                    >
                      <LoadingButton
                        color="primary"
                        loadingPosition="start"
                        endIcon={<ArrowForwardIcon />}
                        variant="contained"
                        type="submit"
                        className="primary-button"
                        name="save"
                        disabled={isLoading || methods.formState.isSubmitting}
                      >
                        Next
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          pb: 2,
          flexDirection: smallScreen ? "column" : "row",
          backgroundColor: "#f1f5f8",
          gap: smallScreen ? 1 : 0,
        }}
      >
        <Typography className="font-size-11">
          Copyrights &copy; {new Date().getFullYear()} - Pediaverse Solutions
          Pvt Ltd
        </Typography>

        {/* Powered by + Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <Typography
            className="font-size-11"
            sx={{ whiteSpace: "nowrap", marginTop: "2px" }}
          >
            Powered by
          </Typography>
          <a href="/" target="_blank">
            <img
              src={logo}
              alt="Logo"
              style={{
                height: 20,
                width: "auto",
                objectFit: "contain",
                verticalAlign: "middle",
                cursor: "pointer",
              }}
            />
          </a>
        </Box>

        <Typography className="font-size-11">
          <a
            href="/terms"
            target="_blank"
            className="blue-link"
            style={{ textDecoration: "none" }}
          >
            Terms of Service
          </a>{" "}
          &nbsp;
          <a
            href="/privacy-policy"
            target="_blank"
            className="blue-link"
            style={{ textDecoration: "none" }}
          >
            Privacy Policy
          </a>
        </Typography>
      </Box>
    </>
  );
};

export default ReferBookEntry;
